import { Check, ChevronsUpDown, X } from 'lucide-react';
import * as React from 'react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export type ComboboxItem = {
  value: string;
  label: string;
  section?: string;
  suffix?: ReactNode;
};

export type ComboboxProps = {
  items: ComboboxItem[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  /**
   * Overrides the dropdown's width/appearance. The default 200px is too narrow
   * for long option labels (file paths, IANA timezones), which would otherwise
   * be truncated with no way to widen them from the call site.
   */
  contentClassName?: string;
  disabled?: boolean;
  allowCustomValues?: boolean; // New prop to enable custom values
};

export type MultiComboboxProps = {
  items: ComboboxItem[];
  value?: string[];
  onChange?: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  allowCustomValues?: boolean;
  maxItems?: number; // Maximum number of items that can be selected
  showSelectedCount?: boolean; // Whether to show count of selected items
};

export const Combobox: React.FC<ComboboxProps> = ({
  items,
  value,
  onChange,
  placeholder = 'Select item...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items found.',
  className,
  contentClassName,
  disabled = false,
  allowCustomValues = false, // Default to false for backward compatibility
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [customItems, setCustomItems] = React.useState<ComboboxItem[]>([]);

  // Combine original items with custom items
  const allItems = React.useMemo(
    () => [...items, ...customItems],
    [items, customItems]
  );

  // Filter and group items by section
  const groupedItems = React.useMemo(() => {
    const groups: Record<string, ComboboxItem[]> = {};

    // Filter items based on search value
    const filteredItems = allItems.filter((item) => {
      if (!searchValue) {
        return true;
      }
      const searchLower = searchValue.toLowerCase();
      return (
        item.label.toLowerCase().includes(searchLower) ||
        item.value.toLowerCase().includes(searchLower)
      );
    });

    filteredItems.forEach((item) => {
      const section = item.section || 'default';
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(item);
    });

    return groups;
  }, [allItems, searchValue]);

  // Find the selected item
  const selectedItem = allItems.find((item) => item.value === value);

  const handleSelect = (selectedValue: string) => {
    const newValue = selectedValue === value ? '' : selectedValue;
    onChange?.(newValue);
    setOpen(false);
    setSearchValue(''); // Clear search when selection is made
  };

  // Handle input change
  const handleInputChange = (inputValue: string) => {
    setSearchValue(inputValue);
  };

  // Handle key down events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && allowCustomValues && searchValue.trim()) {
      // Prevent default form submission
      e.preventDefault();

      // Check if the search value matches an existing option
      const existingItem = allItems.find(
        (item) =>
          item.value.toLowerCase() === searchValue.toLowerCase() ||
          item.label.toLowerCase() === searchValue.toLowerCase()
      );

      if (existingItem) {
        // Select the existing item
        handleSelect(existingItem.value);
      } else {
        // Create and select a new item with the search value
        const newCustomItem: ComboboxItem = {
          value: searchValue.trim(),
          label: searchValue.trim(),
          section: 'Custom', // Optional: group custom items in their own section
        };

        // Add to custom items list
        setCustomItems((prev) => [...prev, newCustomItem]);

        // Select the new item
        handleSelect(searchValue.trim());
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-[200px] min-w-[200px] justify-between active:scale-100',
            className
          )}
          disabled={disabled}
        >
          <span className="text-sm text-foreground/70">
            {selectedItem ? selectedItem.label : value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-[200px] p-0', contentClassName)}>
        <Command value={searchValue}>
          <CommandInput
            value={searchValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>
              {emptyMessage}
              {allowCustomValues && searchValue.trim() && (
                <div className="py-2 text-sm text-muted-foreground">
                  Press Enter to select "{searchValue}"
                </div>
              )}
            </CommandEmpty>
            {Object.entries(groupedItems).map(([section, sectionItems]) => (
              <CommandGroup
                key={section}
                heading={section !== 'default' ? section : undefined}
              >
                {sectionItems.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.value}
                    onSelect={handleSelect}
                  >
                    <span className="flex items-center justify-between flex-1">
                      <span>{item.label}</span>
                      {item.suffix}
                    </span>
                    <Check
                      className={cn(
                        'ml-auto h-4 w-4',
                        value === item.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const MultiCombobox: React.FC<MultiComboboxProps> = ({
  items,
  value = [],
  onChange,
  placeholder = 'Select items...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items found.',
  className,
  disabled = false,
  allowCustomValues = false,
  maxItems,
  showSelectedCount = true,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [customItems, setCustomItems] = React.useState<ComboboxItem[]>([]);

  // Combine original items with custom items
  const allItems = React.useMemo(
    () => [...items, ...customItems],
    [items, customItems]
  );

  // Filter and group items by section
  const groupedItems = React.useMemo(() => {
    const groups: Record<string, ComboboxItem[]> = {};

    // Filter items based on search value
    const filteredItems = allItems.filter((item) => {
      if (!searchValue) {
        return true;
      }
      const searchLower = searchValue.toLowerCase();
      return (
        item.label.toLowerCase().includes(searchLower) ||
        item.value.toLowerCase().includes(searchLower)
      );
    });

    filteredItems.forEach((item) => {
      const section = item.section || 'default';
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(item);
    });

    return groups;
  }, [allItems, searchValue]);

  // Get selected items
  const selectedItems = allItems.filter((item) => value.includes(item.value));

  const handleSelect = (selectedValue: string) => {
    const isSelected = value.includes(selectedValue);
    let newValue: string[];

    if (isSelected) {
      // Remove item if already selected
      newValue = value.filter((v) => v !== selectedValue);
    } else {
      // Add item if not selected and under max limit
      if (maxItems !== undefined && value.length >= maxItems) {
        return; // Don't add if at max limit
      }
      newValue = [...value, selectedValue];
    }

    onChange?.(newValue);
    setSearchValue(''); // Clear search when selection is made
  };

  // Handle input change
  const handleInputChange = (inputValue: string) => {
    setSearchValue(inputValue);
  };

  // Handle key down events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && allowCustomValues && searchValue.trim()) {
      // Prevent default form submission
      e.preventDefault();

      // Check if the search value matches an existing option
      const existingItem = allItems.find(
        (item) => item.value === searchValue || item.label === searchValue
      );

      if (existingItem) {
        // Select the existing item
        handleSelect(existingItem.value);
      } else {
        // Create and select a new item with the search value
        const newCustomItem: ComboboxItem = {
          value: searchValue.trim(),
          label: searchValue.trim(),
          section: 'Custom', // Optional: group custom items in their own section
        };

        // Add to custom items list
        setCustomItems((prev) => [...prev, newCustomItem]);

        // Select the new item
        handleSelect(searchValue.trim());
      }
    }
  };

  // Handle removing a selected item
  const handleRemoveItem = (itemValue: string) => {
    const newValue = value.filter((v) => v !== itemValue);
    onChange?.(newValue);
  };

  // Get display text for the trigger button
  const getDisplayText = () => {
    if (value.length === 0) {
      return placeholder;
    }

    if (showSelectedCount && value.length > 2) {
      return `${value.length} items selected`;
    }

    if (value.length === 1) {
      const item = selectedItems[0];
      return item ? item.label : value[0];
    }

    // Show first two items
    const firstTwo = selectedItems.slice(0, 2);
    return firstTwo.map((item) => item.label).join(', ');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-[200px] min-w-[200px] justify-between active:scale-100',
            className
          )}
          disabled={disabled}
        >
          <div className="flex gap-1 max-w-[150px] overflow-hidden">
            {value.length > 0 && value.length <= 2 ? (
              // Show selected items as chips when 1-2 items selected
              selectedItems.map((item) => (
                <span
                  key={item.value}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-secondary rounded-md"
                >
                  {item.label}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveItem(item.value);
                    }}
                    className="hover:bg-secondary-foreground/20 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            ) : (
              // Show text for 0 or 3+ items
              <span className="text-sm text-foreground/70 truncate">
                {getDisplayText()}
              </span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command value={searchValue}>
          <CommandInput
            value={searchValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>
              {emptyMessage}
              {allowCustomValues && searchValue.trim() && (
                <div className="py-2 text-sm text-muted-foreground">
                  Press Enter to select "{searchValue}"
                </div>
              )}
            </CommandEmpty>
            {Object.entries(groupedItems).map(([section, sectionItems]) => (
              <CommandGroup
                key={section}
                heading={section !== 'default' ? section : undefined}
              >
                {sectionItems.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.value}
                    onSelect={handleSelect}
                    disabled={Boolean(
                      maxItems !== undefined &&
                        !value.includes(item.value) &&
                        value.length >= maxItems
                    )}
                  >
                    <span className="flex items-center justify-between flex-1">
                      <span>{item.label}</span>
                      {item.suffix}
                    </span>
                    <Check
                      className={cn(
                        'ml-auto h-4 w-4',
                        value.includes(item.value) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
